import { CONTACT_METHOD_KINDS, CONTACT_METHOD_KIND_LABEL_KEYS, EMAIL_KINDS, contactRowReach, emptyNote, fill, reachLinesFrom, type ReachLine, mapsSearchHref, noteRepliesFrom, t, telHref, type ContactMethodKind, type IconMeaning, type NoteDraft, type TileDraws, type TileEventRow, DEFAULT_DATE_FORMAT, formatDate, formatDateOnly, tileRowLine, taxExemptBadges, certificateCard, jobTimeline, type DateFormat, type TaxExemptFact, type TileRowPart } from '@bms2/shared';
import type { TileItem, TilePill, TileRowChoice, TileRowLink } from '../../components/composites/Tile';
import type { RowMenuEntry } from '../../components/composites/RowMenu';
import type { FileRowScan, FileRowVerified } from '../../components/composites/FileRow';
import type { NoteThreadProps } from '../../components/composites/NoteThread';
import { whenText } from '../../shell/viewerDates';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import { createElement } from 'react';
import { SuggestionDot } from '../../components/composites/SuggestionDot';

/**
 * A record-page tile's rows, drawn through the Tile's own item types (piece
 * 13). 🔨 The mapping is data, decided by the tile's `draws` in the shared
 * register, so a Company's Contacts is the Person item over the Attendee
 * Avatar and its Sites the Location item — the same renderers the Component
 * Test Page shows. 💰 Every tile was drawn as one-line notes joined with
 * dashes, which matched neither the figures nor those samples (found while
 * building, 2026-09-09).
 */
export interface TileRow {
  label: string;
  value?: string | null;
  detail?: string | null;
  route?: string | null;
  id?: string | null;
  /** 🔒 False on a document row whose page would refuse this viewer, which then carries no route (decision 10370, building 10240). */
  mayOpen?: boolean;
  avatarColor?: string | null;
  /** 🔒 T3028: the person the row names holds an open suggestion, so the row draws the gold dot. */
  suggestions?: boolean;
  /** 🔒 T3076 (decision 7390): the certificates that exempt this site today, drawn as the gold badge beside its role pill. */
  taxExempt?: TaxExemptFact[];
  pill?: string | null;
  pillGroup?: 'good' | 'warning' | 'error' | 'inactive' | null;
  /** A tinted non-status chip beside the row's name: a file's own type (decision 0376). */
  chip?: string | null;
  /** A small pill in front of the status pill, saying who a row is narrowed to: a file's restricting document class (piece 9). */
  flag?: string | null;
  flagGroup?: 'good' | 'warning' | 'error' | 'inactive' | null;
  author?: string | null;
  /** 🔨 An instant as ISO, drawn here in the viewer's zone with the zone named; a row with a span carries its end as `until` (piece 13). */
  at?: string | null;
  /** 🔒 A calendar day (`YYYY-MM-DD`) a History row is dated, drawn as a date in the viewer's format with no zone and no clock (decision 7580). */
  day?: string | null;
  until?: string | null;
  /**
   * 🔒 The value and the detail as parts, each date a date (decision 6701; T2973): drawn here in the viewer's own
   * format, in place of the text the server also sends for a reader that does not read the parts yet.
   */
  valueParts?: TileRowPart[] | null;
  detailParts?: TileRowPart[] | null;
  /** A row that reads muted, such as a NotApplicable supplier (decision 0337). */
  muted?: boolean;
  /** 🔒 The value is a formatted money amount, so a negative figure in it reads red; no other text is scanned for one (decision 2155). */
  money?: boolean;
  /**
   * The heading this row sits under, where the tile's rows are grouped: a
   * sub-skill row carries its skill (product owner, 2026-09-20). Rows arrive in
   * heading order and each heading is drawn once, over the rows beneath it.
   */
  heading?: string | null;
  /**
   * The street address this row's Open in Maps hands over, on one line. 🔨 It is
   * the address and never the building's name: a name is a search and an address
   * is a place (product owner, 2026-09-10).
   */
  mapsQuery?: string | null;
  /** An Upcoming Events row: the whole event, drawn by the Condensed Event Booking row rather than an item (piece 4). */
  event?: TileEventRow | null;
  /** The row's own editable facts, for a tile whose rows are edited in place (decision 0296). */
  raw?: Record<string, string | number | boolean | null>;
}

export interface TileAnswer {
  /** Banner-placed rows draw as pills and chips in the record banner, never on the canvas (decision 0307). */
  placement?: 'canvas' | 'banner';
  key: string;
  label: string;
  owner: 'here' | 'later';
  draws?: TileDraws;
  span?: 1 | 2 | 3;
  height?: number;
  action?: string;
  /** 🔒 The tile folds to its own heading, and the choice sticks across record pages (decision 0500). */
  collapsible?: boolean;
  /** 🔒 How many rows the tile draws before it offers the rest (decision 0500). */
  pageSize?: number;
  /** 🔒 How many History entries stand before the list scrolls inside (T588; decision 1404). */
  windowRows?: number;
  built: boolean;
  masked: boolean;
  rows?: TileRow[];
}

/** A row-level action a tile offers on one of its rows, such as closing an open visit. */
export interface RowActions {
  /** 🔒 A press on a document row the viewer cannot open: the page shows its Permission denied banner (decision 10370, building 10240). */
  onRefused?: () => void;
  /** The File Access Proxy address for a file row, built by the page that knows the API's base. */
  fileHref?: (fileId: string) => string;
  onRowAction?: (rowId: string) => void;
  /** File details on a file row: the type, the description, a note, a new version, delete (decision 0273). */
  onFileEdit?: (row: TileRow) => void;
  /** Share on a file row: the one Share modal on that file, on every Files tile (decision 0345). */
  onFileShare?: (row: TileRow) => void;
  /** A pencil on a full-width row, for a row that opens its own modal loaded (piece 17, Tile 5). */
  onRowEdit?: (row: TileRow) => void;
  rowEditLabel?: string;
  /** The pencil's label for one row, where a tile's rows open different windows: the Employments tile's Set level (T726). */
  rowEditLabelFor?: (row: TileRow) => string;
  /** Which rows take the edit; every row with an id when absent. */
  rowEditWhen?: (row: TileRow) => boolean;
  rowActionLabel?: string;
  /** The action's label for one row, where it names the row's own target: a site's Fold into (decision 1920). */
  rowActionLabelFor?: (row: TileRow) => string;
  rowActionWhen?: (row: TileRow) => boolean;
  /** Drawn as this glyph in the tag chip's manner rather than a labelled button: the contact row's Remove (product owner, 2026-09-12). */
  rowActionIcon?: IconMeaning;
  /** 🔒 The narrow File row's glyph for the row's labelled action, where a Files tile carries one before Share: the Deal's Release (decision 6040). */
  rowActionGlyph?: IconMeaning;
  /** 🔒 Delete this link on a row the server says this viewer may delete (T728; decision 1500). */
  onRowDelete?: (row: TileRow) => void;
  rowDeleteWhen?: (row: TileRow) => boolean;
  /**
   * 🔒 The replies beneath a note (decision 0671): sends one and answers the refusal
   * in words, or null once saved. A page that passes none draws no thread.
   */
  onNoteReply?: (noteId: string, body: string) => Promise<string | null>;
  onNoteReplyDelete?: (noteId: string, replyId: string) => Promise<string | null>;
  /** The threads the page holds open, so a reply that reads the notes again keeps its thread open. */
  openThreads?: ReadonlySet<string>;
  onToggleThread?: (noteId: string) => void;
  /**
   * 🔒 Former sites (decision 1920): a place tile whose closed rows the server
   * marks `former` draws its running rows first and folds the rest under one
   * disclosure; the page holds whether it is open.
   */
  formerOpen?: boolean;
  onToggleFormer?: () => void;
  /** 🔒 Decision 7891: the acts a certificate card's ⋯ menu holds between View Certificate and End, Move and Remove. */
  rowMenuActs?: (row: TileRow) => { key: string; label: string; onAct: () => void; separated?: boolean }[];
}

/**
 * The thread a note row carries, where the page can write replies (decision 0671):
 * its replies oldest first, each time in the viewer's own zone. 🔨 One builder for
 * the tile's Note item and the document page's panel, so the two draw one thread.
 */
export function noteThreadOf(row: TileRow, zone: string, actions: RowActions, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): NoteThreadProps | null {
  const { onNoteReply, onNoteReplyDelete } = actions;
  if (!row.id || !onNoteReply || !onNoteReplyDelete) return null;
  const noteId = row.id;
  return {
    noteId,
    replies: noteRepliesFrom(row.raw?.repliesJson).map((reply) => ({ id: reply.id, author: reply.author, when: whenText(reply.at, zone, dateFormat), body: reply.body, mayDelete: reply.mayDelete })),
    onReply: (body) => onNoteReply(noteId, body),
    onDelete: (replyId) => onNoteReplyDelete(noteId, replyId),
    ...(actions.openThreads && actions.onToggleThread ? { open: actions.openThreads.has(noteId), onToggle: () => actions.onToggleThread?.(noteId) } : {}),
  };
}

function pillOf(row: TileRow): TilePill | null {
  return row.pill ? { text: row.pill, group: row.pillGroup ?? 'inactive' } : null;
}

/**
 * 🔒 Rows T2779 and T2794: the chip, the restriction flag and the status pill of a file row, and the record it came through.
 * A file of one of the company's Deals, Employments or Asset Nodes, listed on the Company's Files tile, names that record
 * in a label of its own beside its type (decisions 6040, 6041), so the type keeps the chip, the restriction the flag and
 * Quarantined the pill.
 */
function fileOriginOf(row: TileRow): { chip: string | null; origin: string | null; flag: TilePill | null; pill: TilePill | null } {
  const status = row.pill === 'Verified' || row.pill === 'Unverified' ? null : pillOf(row);
  const origin = typeof row.raw?.originName === 'string' && row.raw.originName !== '' ? row.raw.originName : null;
  return { chip: row.chip ?? null, origin, flag: flagOf(row), pill: status ?? releasePillOf(row) };
}

/**
 * 🔒 Decision 8960: a Deal's file whose newest open release waits for approval reads Pending Approval, and one whose
 * release was rejected reads Release Rejected; Quarantined, a warning about the file itself, keeps the pill first.
 */
function releasePillOf(row: TileRow): TilePill | null {
  return releasePillFor(row.raw?.releaseApproval);
}

/** The release pill for an approval standing, which the tile's row and a document's file row both draw (decisions 8960, 11480). */
export function releasePillFor(approval: unknown): TilePill | null {
  if (approval === 'Pending') return { text: t('file.release.pending'), group: 'warning' };
  if (approval === 'Rejected') return { text: t('file.release.rejected'), group: 'error' };
  return null;
}

/**
 * 🔒 A file row's labelled actions (decision 6040): where the tile offers a row action, such as the Deal's Release, it is
 * the first button and Share the second, the order the full-width row drew them in; elsewhere Share is the one button.
 */
function fileButtonsOf(row: TileRow, actions: RowActions): Pick<Extract<TileItem, { type: 'file' }>, 'button' | 'secondButton'> {
  const id = row.id;
  const share = id && actions.onFileShare ? { label: t('file.share'), onAct: () => actions.onFileShare!(row) } : null;
  const offered = id && actions.onRowAction && actions.rowActionLabel && (actions.rowActionWhen?.(row) ?? true);
  if (!offered) return { button: share, secondButton: null };
  return { button: { label: actions.rowActionLabelFor?.(row) ?? actions.rowActionLabel!, onAct: () => actions.onRowAction!(id), icon: actions.rowActionGlyph ?? 'moreActions' }, secondButton: share };
}

/** The row's restriction pill, which stands before its status pill where it has one (piece 9). */
function flagOf(row: TileRow): TilePill | null {
  return row.flag ? { text: row.flag, group: row.flagGroup ?? 'inactive' } : null;
}

/**
 * A row's own reach, derived from what the row already holds rather than from
 * the action registry: an address opens a maps search, an email address opens a
 * message, a desk or mobile line opens the dialer. 🔨 The maps destination is
 * the shared one every Open in Maps uses, so the event card and a record row
 * send the reader to the same place. 💰 A Building row drew an address nobody
 * could act on, and a contact row offered a pencil and a × and no way to use the
 * address or the number at all (product owner, 2026-09-14).
 *
 * 🔨 A Fax carries no call and an Other carries nothing: neither is a line a
 * person dials. The extension is left off the dialer link and stays readable in
 * the row, because a `tel:` a handset refuses to open is worse than one it opens
 * short of the extension.
 */
export function reachesOf(row: TileRow): TileRowLink[] {
  const links: TileRowLink[] = [];
  const maps = mapsSearchHref(row.mapsQuery ?? null);
  if (maps) links.push({ href: maps, label: t('record.tile.openInMaps'), icon: 'location', newTab: true });
  const kind = typeof row.raw?.kind === 'string' ? (row.raw.kind as ContactMethodKind) : null;
  const value = typeof row.raw?.value === 'string' ? row.raw.value.trim() : '';
  if (kind !== null && value !== '') {
    const extension = typeof row.raw?.extension === 'string' ? row.raw.extension : null;
    if (EMAIL_KINDS.includes(kind)) links.push({ href: `mailto:${value}`, label: t('record.tile.emailAddress'), icon: 'mail' });
    else if (kind === 'Desk' || kind === 'Mobile') links.push({ href: telHref(value, extension), label: t('record.tile.callNumber'), icon: 'callNumber' });
  }
  /**
   * 🔨 A row that stands for a person carries both of their lines, so a contact is
   * called or written to from the list itself (product owner, 2026-09-20). The call
   * comes first: it is the one a person reaches for on a phone.
   */
  /**
   * 🔒 A row that carries every one of its lines offers the first of each reach on
   * its button and the rest behind a chevron beside it, primary first (T214;
   * decision 0692). A row carrying one line of a reach draws the plain button.
   */
  const lines = reachLinesFrom(row.raw?.reachJson);
  if (lines.length > 0) {
    const calls = lines.filter((line) => line.kind === 'Desk' || line.kind === 'Mobile');
    const writes = lines.filter((line) => EMAIL_KINDS.includes(line.kind));
    if (calls[0]) links.push({ href: contactRowReach(calls[0].kind, calls[0].value, calls[0].extension).href, label: t('record.tile.callNumber'), icon: 'callNumber', text: t('record.call'), tone: 'call', ...choicesOf(calls) });
    if (writes[0]) links.push({ href: contactRowReach(writes[0].kind, writes[0].value).href, label: t('record.tile.emailAddress'), icon: 'mail', text: t('record.email'), tone: 'email', ...choicesOf(writes) });
    return links;
  }
  const phone = typeof row.raw?.phone === 'string' ? row.raw.phone.trim() : '';
  const email = typeof row.raw?.email === 'string' ? row.raw.email.trim() : '';
  // 🔒 The row's own Call and Email read as the contact row's do (decision 0623): worded, the Call filled green.
  if (phone !== '') links.push({ href: telHref(phone), label: t('record.tile.callNumber'), icon: 'callNumber', text: t('record.call'), tone: 'call' });
  if (email !== '') links.push({ href: `mailto:${email}`, label: t('record.tile.emailAddress'), icon: 'mail', text: t('record.email'), tone: 'email' });
  return links;
}

/**
 * The lines behind a reach's chevron, each named the way the contact row names
 * it — the kind, the line's own word, and its primary mark (decision 0623). 🔨 One line is no choice, so it carries none
 * and the button draws plain.
 */
function choicesOf(lines: readonly ReachLine[]): { choices?: TileRowChoice[] } {
  if (lines.length < 2) return {};
  return {
    choices: lines.map((line) => ({
      href: contactRowReach(line.kind, line.value, line.extension).href,
      name: [t(CONTACT_METHOD_KIND_LABEL_KEYS[line.kind]), line.label, line.isPrimary ? t('contact.primary') : null].filter(Boolean).join(' · '),
      value: line.display,
    })),
  };
}

/** The one reach a single-valued row has, which is the first of the above: a Label/Value pair carries one value and one way to use it. */
/** 🔒 A document row the viewer cannot open takes the page's Permission denied press in place of its link (decision 10370). */
function refusedOf(row: TileRow, actions: RowActions): { onRefused?: () => void } {
  return row.mayOpen === false && actions.onRefused ? { onRefused: actions.onRefused } : {};
}

export function reachOf(row: TileRow): TileRowLink | null {
  return reachesOf(row)[0] ?? null;
}

/**
 * A dated role's span in the reader's own date format, the open end reading
 * Present. 💰 The row printed the stored `2026-01-05 — open` for weeks, which is
 * a database value and not a sentence (product owner, 2026-09-14).
 */
function roleSpan(row: TileRow, format: DateFormat): string | null {
  if (!row.at) return row.detail ?? null;
  return `${formatDateOnly(row.at, format)} – ${row.until ? formatDateOnly(row.until, format) : t('record.tile.present')}`;
}

/**
 * A row's arrival and its departure, as the reader sees them, each one its own
 * line. 🔨 The two instants are never joined into one string: a span drawn as
 * `5:00 PM EST — 6:30 PM EST` wraps wherever the row runs out of width, and the
 * break lands mid-range as often as not (product owner, 2026-09-13).
 */
function spanOf(row: TileRow, zone: string, format: DateFormat): { from: string; until: string | null } | null {
  if (!row.at) return null;
  return { from: whenText(row.at, zone, format), until: row.until ? whenText(row.until, zone, format) : null };
}

/**
 * 🔒 The stacked contact row every phone and email item draws through
 * (decision 0623): the kind's glyph, its human-English name over the job the
 * line is held in, the value, and the one action the line offers. *"Example Desk
 * Phone, ABS Inc. Phone in Middle Then Call on right"* — product owner,
 * 2026-09-22.
 *
 * 🔨 The name is the kind's, from the one label map, and never the stored
 * member. The row's own word for itself and its primary mark read beside it,
 * lighter, because a person with two desk lines needs them told apart (T147).
 */
function contactItemOf(row: TileRow, actions: RowActions): TileItem {
  const raw = row.raw ?? {};
  // 🔒 Decision 2972: a company's mailbox draws as a contact line, its kind as the name and Email on the right.
  if (typeof raw.mailboxKind === 'string') return mailboxItemOf(row, actions);
  const kind = typeof raw.kind === 'string' && (CONTACT_METHOD_KINDS as readonly string[]).includes(raw.kind) ? (raw.kind as ContactMethodKind) : null;
  const stored = typeof raw.value === 'string' ? raw.value : '';
  const extension = typeof raw.extension === 'string' ? raw.extension : null;
  const reach = kind ? contactRowReach(kind, stored, extension) : { action: 'none' as const, href: '' };
  const qualifier = [typeof raw.label === 'string' ? raw.label : null, raw.isPrimary === true ? t('contact.primary') : null].filter(Boolean).join(' · ');
  return {
    type: 'contactRow',
    // A row whose kind the server did not name still draws: the server's own label stands in.
    name: kind ? t(CONTACT_METHOD_KIND_LABEL_KEYS[kind]) : row.label,
    // 🔨 The job the line belongs to, muted under the name: the company and the title where the row has one (decision 0593).
    caption: (typeof raw.jobLabel === 'string' && raw.jobLabel !== '' ? raw.jobLabel : row.detail) ?? null,
    ...(qualifier ? { qualifier } : {}),
    value: row.value ?? stored,
    icon: kind ? CONTACT_ROW_ICONS[kind] : null,
    action: reach.action === 'none' ? null : { tone: reach.action, href: reach.href, label: t(CONTACT_ACTION_LABELS[reach.action]), ...(reach.action === 'web' ? { newTab: true } : {}) },
    onEdit: actions.onRowEdit && row.id && (actions.rowEditWhen ? actions.rowEditWhen(row) : true) ? () => actions.onRowEdit?.(row) : null,
    ...(actions.rowEditLabel ? { editLabel: actions.rowEditLabel } : {}),
  };
}

/** 🔒 A Mailboxes tile row (decision 2972): the kind's name, the row's own label beside it, the address and Email; the pencil opens its window. */
function mailboxItemOf(row: TileRow, actions: RowActions): TileItem {
  const raw = row.raw ?? {};
  const address = typeof raw.value === 'string' ? raw.value : (row.value ?? '');
  const reach = contactRowReach('WorkEmail', address);
  return {
    type: 'contactRow',
    name: row.label,
    caption: null,
    ...(typeof raw.label === 'string' && raw.label !== '' ? { qualifier: raw.label } : {}),
    value: row.value ?? address,
    icon: 'mail',
    action: reach.action === 'none' ? null : { tone: reach.action, href: reach.href, label: t(CONTACT_ACTION_LABELS[reach.action]) },
    onEdit: actions.onRowEdit && row.id ? () => actions.onRowEdit?.(row) : null,
    ...(actions.rowEditLabel ? { editLabel: actions.rowEditLabel } : {}),
  };
}

/**
 * The glyph of the kind, at the row's far left. 🔨 An Other line that is a note
 * carries none: a glyph that stood for *something else* would say less than the
 * empty slot the row already keeps to hold its columns straight.
 */
const CONTACT_ROW_ICONS: Readonly<Record<ContactMethodKind, IconMeaning | null>> = {
  WorkEmail: 'mail',
  PersonalEmail: 'mail',
  Desk: 'callNumber',
  Mobile: 'mobile',
  Fax: 'print',
  Other: null,
};

const CONTACT_ACTION_LABELS = { call: 'record.call', email: 'record.email', web: 'record.openWeb' } as const;

/**
 * 🔒 One note row in the Note item's shape (decision 0950): the Internal Notes tile
 * and a Deal stage-card row's list both read their rows through it, so the author,
 * the time in the viewer's zone and the pencil stand in one place on both.
 */
export function noteItemOf(row: TileRow, zone: string, actions: RowActions = {}, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): Extract<TileItem, { type: 'note' }> {
  return {
    type: 'note',
    id: row.id ?? null,
    text: row.label,
    author: row.author ?? null,
    // 🔨 The row says what the note is about where it was not written on this record: the
    // file a note in File details hangs on, or the record a linked note was written on
    // (decisions 0339, 0605). 💰 The detail was carried and drawn nowhere, so the marking
    // the plan promised existed only in the modal (found while building, 2026-09-21).
    when: [whenText(row.at, zone, dateFormat) || null, row.detail || null, row.raw?.edited === true ? t('note.edited') : null].filter(Boolean).join(' · ') || null,
    pill: pillOf(row),
    issue: row.pill !== null && row.pill !== undefined,
    onEdit: actions.onRowEdit && row.id && (actions.rowEditWhen ? actions.rowEditWhen(row) : true) ? () => { actions.onRowEdit?.(row); } : null,
    ...(actions.rowEditLabel ? { editLabel: actions.rowEditLabel } : {}),
    thread: noteThreadOf(row, zone, actions, dateFormat),
    // 🔒 A note written on a Deal stage-card row names its section (decision 0725).
    section: row.chip ?? null,
  };
}

/**
 * 🔒 A row whose value or detail arrives as parts reads them in the viewer's own date format (decision 6701), so a
 * span reads 10/21/2026 – Present beside the Employments tile's and never the server's ISO text.
 */
export function withRowDates(row: TileRow, zone: string, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): TileRow {
  if (!row.valueParts && !row.detailParts) return row;
  return {
    ...row,
    ...(row.valueParts ? { value: tileRowLine(row.valueParts, zone, dateFormat) || null } : {}),
    ...(row.detailParts ? { detail: tileRowLine(row.detailParts, zone, dateFormat) || null } : {}),
  };
}

/** The rows a tile shows, in the item type the register names for it. */
export function itemsFor(tile: TileAnswer, zone: string, actions: RowActions = {}, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): TileItem[] {
  if (!tile.built) return [{ type: 'note', text: t('record.tile.later') }];
  // An answer that carries no rows for a tile draws the empty note rather than
  // throwing: one missing key must not take the whole canvas down with it.
  const rows = (tile.rows ?? []).map((row) => withRowDates(row, zone, dateFormat)).filter((row) => row.label !== '' || row.value);
  if (rows.length === 0) return [{ type: 'note', text: t('record.tile.empty') }];
  const draws: TileDraws = tile.draws ?? 'fullWidthRow';

  switch (draws) {
    case 'labelValueGrid': {
      // A pair whose value is an instant carries it as `at`, and it is drawn here in the viewer's zone (piece 13).
      const pairs = rows
        .map((row) => ({ ...row, value: row.value ?? (row.at ? whenText(row.at, zone, dateFormat) : null) }))
        .filter((row) => row.value !== null && row.value !== undefined && row.value !== '')
        // 🔒 Decision 3280: a pair keeps the status pill its row carries, which the grid dropped until then.
        .map((row) => ({ label: row.label, value: String(row.value), route: row.route ?? null, link: reachOf(row), ...(row.money ? { money: true } : {}), ...(row.pill ? { pill: pillOf(row) } : {}), ...controlsOf(row, actions) }));
      return pairs.length === 0 ? [{ type: 'note', text: t('record.tile.empty') }] : [{ type: 'labelValueGrid', pairs }];
    }
    case 'person': {
      // 🔒 Decision 11980: the Company's Contacts tile lays each contact as one row, the title on its own line over the detail lines.
      const contactRows = tile.key === 'contacts';
      return rows.map((row) => ({
        type: 'person',
        id: row.id ?? null,
        person: { displayName: row.label, avatarColor: row.avatarColor ?? 'avatar-1' },
        attending: 'NeedsAction',
        // 🔒 T3028: the gold dot beside the name, read off the row's own flag.
        ...(row.suggestions ? { marker: createElement(SuggestionDot) } : {}),
        ...(contactRows ? { row: true, title: row.value ?? null } : {}),
        detail: (contactRows ? [row.detail] : [row.value, row.detail]).filter(Boolean).join(' · ') || null,
        route: row.route ?? null,
        pill: pillOf(row),
        // The call and the message the row's own lines give it, at the row's far right, primary first (product owner, 2026-09-20; decision 0692).
        links: reachesOf(row),
        ...controlsOf(row, actions),
      }));
    }
    case 'location': {
      const place = (row: TileRow): TileItem => ({
        type: 'location',
        name: row.label,
        address: row.value ?? '',
        detail: row.detail ?? null,
        route: row.route ?? null,
        pill: pillOf(row),
        // 🔒 T3076 (decision 7390): the gold badge of each certificate that exempts today, the years in the shared rule and the hover in the viewer's date format.
        badges: taxExemptBadges(row.taxExempt, dateFormat),
        // Open in Maps where the row names a place, through the one maps destination (product owner, 2026-09-20).
        link: reachOf(row),
        ...controlsOf(row, actions),
      });
      // 🔒 Decision 1920: the closed rows fold under Former sites (n), so a closed row never reads as a duplicate beside a running one.
      const former = rows.filter((row) => row.raw?.former === true);
      if (former.length === 0 || !actions.onToggleFormer) return rows.map(place);
      const open = actions.formerOpen === true;
      const toggle = actions.onToggleFormer;
      return [
        ...rows.filter((row) => row.raw?.former !== true).map(place),
        { type: 'fullWidthRow', text: `${t('sites.former')} (${former.length})`, button: { label: open ? t('sites.former.hide') : t('sites.former.show'), onAct: toggle } },
        ...(open ? former.map(place) : []),
      ];
    }
    case 'file':
      // 🔒 Newest first, by the newest version's upload instant each row carries (piece 9; T2438); a row without one keeps its place after them.
      return filesNewestFirst(rows).map((row) => ({
        type: 'file',
        name: row.label,
        // 🔒 Decision 3013: the description in the uploader's words and the version it is on, which the row places itself; a row with no raw description keeps its detail line.
        description: typeof row.raw?.description === 'string' ? row.raw.description || null : null,
        version: typeof row.raw?.version === 'number' ? row.raw.version : null,
        // A file nobody described sends the key with null, and the joined detail then holds nothing but the version the row already draws.
        detail: row.raw !== undefined && 'description' in row.raw ? null : row.detail || null,
        // 🔒 Decision 3012: the shield and the scanner under a clean file's icon, Scan pending as a waiting file's last line.
        scan: fileScanOf(verdictOf(row.raw?.scanVerdict), typeof row.raw?.scannedAt === 'string' ? row.raw.scannedAt : null, typeof row.raw?.scannedBy === 'string' ? row.raw.scannedBy : null, zone, dateFormat),
        // 🔒 Decision 3012: a verified file's last line carries the verifier's avatar and the word; an unverified file carries nothing.
        verified: row.pill === 'Verified' ? fileVerifiedOf(typeof row.raw?.verifiedByName === 'string' ? { name: row.raw.verifiedByName, avatarColor: typeof row.raw?.verifiedByAvatarColor === 'string' ? row.raw.verifiedByAvatarColor : 'avatar-1' } : null, typeof row.raw?.verifiedAt === 'string' ? row.raw.verifiedAt : null, zone, dateFormat) : null,
        // 🔒 Decision 1182: a quarantined file's sentence, on a line of its own in the one File row (decision 3010).
        quarantine: typeof row.raw?.quarantine === 'string' ? row.raw.quarantine : null,
        // 🔒 Decision 7420: the lock beside the name of a file whose bytes are stored encrypted.
        encrypted: row.raw?.encrypted === true,
        route: row.route ?? null,
        href: row.id && actions.fileHref ? actions.fileHref(row.id) : null,
        // 🔒 The media type the row's icon is picked by, with the name's extension (decision 3011).
        mimeType: typeof row.raw?.mimeType === 'string' ? row.raw.mimeType : null,
        // 🔒 Decision 11301: the date the person gave the document at its upload, in the viewer's date format.
        dated: typeof row.raw?.documentDate === 'string' ? fill(t('file.dated'), { date: formatDateOnly(row.raw.documentDate, dateFormat) }) : null,
        // 🔒 Share on every file row, and on the Deal's after Release (decisions 0345, 6040).
        ...fileButtonsOf(row, actions),
        onEdit: row.id && actions.onFileEdit ? () => actions.onFileEdit!(row) : null,
        // The type reads as a chip and the restriction as a small pill (decision 0376; piece 9).
        // 🔒 Decision 3012: the Verified and Unverified pill leaves the row, since the Verified line says it; the Quarantined pill stays, a warning.
        // 🔒 Rows T2779, T2794: a related record's file on the Company's Files tile carries that record's name as its own label beside its type (decisions 6040, 6041).
        ...fileOriginOf(row),
      }));
    case 'note':
      return rows.map((row) => noteItemOf(row, zone, actions, dateFormat));
    case 'statusOnly':
      return rows.map((row) => ({ type: 'statusOnly', label: row.label, entityType: row.value === 'flag' ? 'RecordFlag' : 'Tag', statusValue: row.pill ?? row.label }));
    case 'datedRole':
      return rows.map((row) => ({
        type: 'datedRole' as const,
        role: row.label,
        organization: row.value ?? null,
        span: roleSpan(row, dateFormat),
        route: row.route ?? null,
        pill: pillOf(row),
        ...controlsOf(row, actions),
      }));
    /**
     * 🔒 T3097 (decision 7480): a person's Employments as a timeline. The rows are one per position; the shared fold makes one job per
     * employment, and the job's pencil, pill and remove are the newest position's row's, as the registry gives them.
     */
    case 'jobTimeline':
      return [{
        type: 'jobTimeline' as const,
        jobs: jobTimeline(rows, dateFormat).map((job) => {
          const controls = controlsOf(job.lead, actions);
          return { key: job.key, company: job.company, route: job.route, span: job.span, running: job.running, pill: job.pill, titles: job.titles, ...controls };
        }),
      },
      // 🔒 Decision 9861 (T3455), T3466: each of the person's agreements beneath the jobs as a dated role, the role first and one caption line
      // reading its state and the date signed, a link to its page; the caption never wraps the state from the date.
      ...rows
        .filter((row) => typeof row.raw?.agreementId === 'string' && row.route)
        .map((row) => ({
          type: 'datedRole' as const,
          role: row.label,
          organization: row.value ?? null,
          span: row.at ? fill(t('person.agreement.from'), { date: formatDateOnly(row.at, dateFormat) }) : null,
          route: row.route!,
          data: { 'data-agreement-row': String(row.id ?? row.route) },
        }))];
    case 'timeline':
      return [
        {
          type: 'timeline',
          entries: rows.map((row) => ({
            when: row.at ? whenText(row.at, zone, dateFormat) : (row.detail ?? ''),
            text: row.label,
            detail: [row.value, row.at ? row.detail : null].filter(Boolean).join(' · ') || null,
            pill: pillOf(row),
            route: row.route ?? null,
            ...controlsOf(row, actions),
          })),
        },
      ];
    // The Event Booking rows draw themselves through the tile's children slot; an item list would be a second construction.
    case 'eventBooking':
      return [];
    case 'linkRow':
      return rows.map((row) =>
        row.route
          ? { type: 'linkRow' as const, label: row.label, route: row.route, value: row.value ?? row.detail ?? null, ...(row.money ? { money: true } : {}), key: `${tile.key}:${row.label}` }
          // 💰 The fallback row dropped the registry's controls until 2026-09-14, so a link row with no route offered nothing (found while building, 2026-09-14).
          : { type: 'fullWidthRow' as const, text: row.label, value: row.value ?? null, ...(row.money ? { money: true } : {}), pill: pillOf(row), ...controlsOf(row, actions) },
      );
    /**
     * 🔒 A document list draws as one grid: the date, the kind of document, its
     * pill and its name, in that order, so every row of the list lines up
     * (decision 0591). The date is the grid's own column and is not folded into
     * the row's text as well.
     */
    case 'documentRow':
      return rows.map((row) => ({
        type: 'documentRow' as const,
        date: row.at ? formatDate(row.at, zone, dateFormat) : '',
        kind: row.value ?? null,
        pill: pillOf(row),
        text: row.label,
        detail: row.detail ?? null,
        route: row.route ?? null,
        ...refusedOf(row, actions),
        ...controlsOf(row, actions),
      }));
    /**
     * 🔒 The contact-row standard (decision 0623): a tile listing a phone or an
     * email draws every row this way, on the Person page and anywhere else a line
     * is listed.
     */
    case 'contactRow':
      return rows.map((row) => contactItemOf(row, actions));
    /**
     * 🔒 T3082 (decision 7440): a certificate is a fact card. Its ⋯ menu holds Edit, View Certificate and End, in that order:
     * Edit and End from the tile's own row controls where the viewer writes, View Certificate for any viewer the file may open
     * to, on an ended card too (its file is still the record of what was exempt), where it stands alone.
     */
    case 'certificateCard':
      return rows.map((row) => {
        const controls = controlsOf(row, actions);
        const card = certificateCard(row, dateFormat, (at) => whenText(at, zone, dateFormat));
        const fileId = typeof row.raw?.fileId === 'string' && row.raw.fileId !== '' ? row.raw.fileId : null;
        const menu: RowMenuEntry[] = [
          ...(controls.onEdit ? [{ key: 'edit', label: controls.editLabel ?? t('certificate.edit'), onAct: controls.onEdit }] : []),
          ...(fileId && actions.fileHref ? [{ key: 'view', label: t('certificate.view'), href: actions.fileHref(fileId) }] : []),
          ...(actions.rowMenuActs?.(row) ?? []),
          ...(controls.button ? [{ key: 'end', label: controls.button.label, onAct: controls.button.onAct, separated: true }] : []),
        ];
        return { type: 'certificateCard' as const, card, menu, menuLabel: t('certificate.menu'), id: row.id ?? null };
      });
    case 'fullWidthRow':
    default:
      return rows.map((row) => ({
        type: 'fullWidthRow' as const,
        ...(row.heading ? { heading: row.heading } : {}),
        text: [row.label, row.detail].filter(Boolean).join(' · '),
        when: spanOf(row, zone, dateFormat),
        value: row.value ?? null,
        ...(row.money ? { money: true } : {}),
        route: row.route ?? null,
        ...refusedOf(row, actions),
        pill: pillOf(row),
        ...(row.muted ? { muted: true } : {}),
        ...controlsOf(row, actions),
      }));
  }
}

/**
 * The Note modal's draft from a note row's own facts, which the tile answer
 * carries beside the text (decision 0301). It sits beside `TileRow` because
 * two screens now reopen a note from one — the record page's Notes tile and a
 * document page's Internal Notes panel (decision 0470).
 */
export function noteDraftFrom(row: TileRow): NoteDraft & { originLabel?: string } {
  const raw = row.raw ?? {};
  const direction = raw.direction === 'Inbound' || raw.direction === 'Outbound' ? raw.direction : null;
  // The records the note is also about ride as JSON on the row, and the note's own origin beside them (decision 0339).
  let links: NoteDraft['links'] = [];
  if (typeof raw.linksJson === 'string') {
    try {
      const parsed = JSON.parse(raw.linksJson) as { recordType?: string; recordId?: string; label?: string }[];
      if (Array.isArray(parsed)) links = parsed.filter((l) => typeof l.recordType === 'string' && typeof l.recordId === 'string').map((l) => ({ recordType: l.recordType!, recordId: l.recordId!, label: l.label ?? l.recordId! }));
    } catch (error) {
      reportCaughtInBrowser('records.tileItems.links', error);
      links = [];
    }
  }
  return {
    ...emptyNote(),
    body: row.label,
    taggedRecordType: typeof raw.originType === 'string' ? raw.originType : null,
    taggedRecordId: typeof raw.originId === 'string' ? raw.originId : null,
    ...(typeof raw.originLabel === 'string' ? { originLabel: raw.originLabel } : {}),
    links,
    issueFlagId: typeof raw.issueFlagId === 'string' ? raw.issueFlagId : null,
    activityKindId: typeof raw.activityKindId === 'string' ? raw.activityKindId : null,
    direction,
    durationMinutes: typeof raw.durationMinutes === 'number' ? raw.durationMinutes : null,
  };
}

/** The one action and the pencil a row offers, from the tile's registry entry (decision 0309); a row with no id offers neither. */
function controlsOf(row: TileRow, actions: RowActions): { button: { label: string; onAct: () => void; icon?: IconMeaning } | null; onEdit: (() => void) | null; editLabel?: string; onDelete?: (() => void) | null } {
  const id = row.id;
  const deletes = actions.onRowDelete && (actions.rowDeleteWhen ? actions.rowDeleteWhen(row) : false);
  return {
    ...(deletes ? { onDelete: () => actions.onRowDelete?.(row) } : {}),
    button: actions.onRowAction && id && (actions.rowActionWhen ? actions.rowActionWhen(row) : true) ? { label: actions.rowActionLabelFor?.(row) ?? actions.rowActionLabel ?? t('record.tile.open'), onAct: () => actions.onRowAction?.(id), ...(actions.rowActionIcon ? { icon: actions.rowActionIcon } : {}) } : null,
    onEdit: actions.onRowEdit && id && (actions.rowEditWhen ? actions.rowEditWhen(row) : true) ? () => actions.onRowEdit?.(row) : null,
    ...(actions.rowEditLabelFor ? { editLabel: actions.rowEditLabelFor(row) } : actions.rowEditLabel ? { editLabel: actions.rowEditLabel } : {}),
  };
}

/** A verdict as the server sends it, or null for anything else. */
function verdictOf(value: unknown): 'Pending' | 'Clean' | 'Blocked' | null {
  return value === 'Pending' || value === 'Clean' || value === 'Blocked' ? value : null;
}

/**
 * 🔒 A Files tile newest first (piece 9; T2438): by the upload instant of each
 * file's newest version, which the row carries as `raw.uploadedAt`. A row from an
 * answer that carries none keeps the server's order after the dated rows.
 */
export function filesNewestFirst<T extends TileRow>(rows: readonly T[]): T[] {
  const at = (row: T): number => (typeof row.raw?.uploadedAt === 'string' ? Date.parse(row.raw.uploadedAt) : Number.NaN);
  return rows
    .map((row, index) => ({ row, index, at: at(row) }))
    .sort((a, b) => {
      const aDated = Number.isFinite(a.at);
      const bDated = Number.isFinite(b.at);
      if (aDated && bDated && a.at !== b.at) return b.at - a.at;
      if (aDated !== bDated) return aDated ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ row }) => row);
}

/**
 * 🔒 Decision 1953: what a version's scan says in words, Scanned with the time in
 * the viewer's zone and the scanner, or Scan pending. A Blocked version's row
 * carries the Quarantined sentence instead, so it says nothing here. Since
 * decision 3012 the words are a clean row's shield title and File details' line,
 * never a line of the row itself.
 */
export function fileScanTitle(verdict: 'Pending' | 'Clean' | 'Blocked' | null, at: string | null, scanner: string | null, zone: string, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): string | null {
  if (verdict === 'Pending') return t('file.scan.pending');
  if (verdict !== 'Clean' || at === null) return null;
  const when = whenText(at, zone, dateFormat);
  const said = scanner ? fill(t('file.scan.scanned'), { scanner }) : t('file.scan.scannedUnnamed');
  return fill(said, { when });
}

/**
 * 🔒 Decision 3012: a file row's scan mark. A Clean version carries the shield
 * and the scanner's name beneath the icon, its date and time in the title; a
 * Pending one reads Scan pending as the row's last line; any other carries none.
 */
export function fileScanOf(verdict: 'Pending' | 'Clean' | 'Blocked' | null, at: string | null, scanner: string | null, zone: string, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): FileRowScan | null {
  if (verdict === 'Pending') return { state: 'pending' };
  if (verdict !== 'Clean') return null;
  return { state: 'clean', scanner, title: fileScanTitle(verdict, at, scanner, zone, dateFormat) ?? t('file.scan.clean') };
}

/**
 * 🔒 Decision 3012: a verified file's last line, the verifier's avatar and the
 * word Verified, with who and when in the title; the word stands alone where no
 * verifier is recorded. An unverified file carries none.
 */
export function fileVerifiedOf(verifier: { name: string; avatarColor: string } | null, at: string | null, zone: string, dateFormat: DateFormat = DEFAULT_DATE_FORMAT): FileRowVerified {
  const date = at ? formatDate(at, zone, dateFormat) : null;
  const title = verifier && date ? fill(t('file.verified.byOn'), { name: verifier.name, date }) : date ? fill(t('file.verified.on'), { date }) : t('file.verified');
  return { person: verifier ? { displayName: verifier.name, avatarColor: verifier.avatarColor } : null, title };
}
